import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const GameCard = ({ game, onEdit, onDelete, isOwnGame = false }) => {
  const navigate = useNavigate();
  
  const handleEdit = () => {
    if (onEdit) {
      onEdit(game);
    } else {
      toast.info('Edit game functionality coming soon!');
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      try {
        await onDelete(game.id);
        toast.success('Game deleted successfully');
      } catch (error) {
        toast.error('Failed to delete game');
      }
    }
  };

  const getResultColor = () => {
    if (game.our_score > game.their_score) return 'bg-green-500';
    if (game.our_score < game.their_score) return 'bg-red-500';
    return 'bg-yellow-500';
  };

  const getResultText = () => {
    if (game.our_score > game.their_score) return 'W';
    if (game.our_score < game.their_score) return 'L';
    return 'T';
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      {/* Player Header */}
      <div className="flex items-center mb-3">
        <button
          onClick={() => navigate(`/app/profile/${game.user?.id}`)}
          className="flex items-center flex-1 hover:bg-gray-50 rounded-lg p-1 -m-1 transition-colors"
        >
          {game.user?.avatar ? (
            <img
              src={game.user.avatar}
              alt="User"
              className="w-10 h-10 rounded-full mr-3"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center mr-3">
              <span className="text-white font-bold text-sm">
                {game.user?.first_name?.[0]}{game.user?.last_name?.[0]}
              </span>
            </div>
          )}
          <div className="flex-1 text-left">
            <p className="font-medium text-gray-900">
              {game.user?.first_name} {game.user?.last_name}
            </p>
            <p className="text-sm text-gray-500">
              {new Date(game.date).toLocaleDateString()}
            </p>
          </div>
        </button>
      </div>

      {/* Game Info */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          {/* Our Team */}
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center mb-2">
              {game.userOrganization?.image_url && (
                <img
                  src={game.userOrganization.image_url}
                  alt="Team"
                  className="w-8 h-8 rounded mr-2 object-contain"
                />
              )}
              <p className="text-sm font-medium text-gray-900">
                {game.userOrganization?.team_name || game.userOrganization?.name || 'Your Team'}
              </p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{game.our_score}</p>
          </div>

          {/* VS Section */}
          <div className="mx-4 text-center">
            <p className="text-sm text-gray-500 mb-1">vs</p>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${getResultColor()}`}>
              {getResultText()}
            </div>
          </div>

          {/* Opponent Team */}
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center mb-2">
              {game.opponent?.image_url && (
                <img
                  src={game.opponent.image_url}
                  alt="Opponent"
                  className="w-8 h-8 rounded mr-2 object-contain"
                />
              )}
              <p className="text-sm font-medium text-gray-900">
                {game.opponent?.team_name || game.opponent?.name || 'Opponent'}
              </p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{game.their_score}</p>
          </div>
        </div>

        {/* Player Stats */}
        <div className="flex justify-around pt-3 border-t border-gray-100 mt-3">
          {game.user?.position === 'goalie' ? (
            // Goalie Stats
            <>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Saves</p>
                <p className="text-sm font-semibold text-gray-900">{game.saves || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">GA</p>
                <p className="text-sm font-semibold text-gray-900">{game.goals_against || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">SV%</p>
                <p className="text-sm font-semibold text-gray-900">
                  {game.shots_against > 0 ? ((game.saves / game.shots_against) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </>
          ) : (
            // Skater Stats
            <>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Goals</p>
                <p className="text-sm font-semibold text-gray-900">{game.goals || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Assists</p>
                <p className="text-sm font-semibold text-gray-900">{game.assists || 0}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Points</p>
                <p className="text-sm font-semibold text-gray-900">{(game.goals || 0) + (game.assists || 0)}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {isOwnGame && (
        <div className="flex justify-end space-x-2">
          <button
            onClick={handleEdit}
            className="text-gray-400 hover:text-blue-600 p-1"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={handleDelete}
            className="text-gray-400 hover:text-red-600 p-1"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GameCard; 